import { useEffect, useRef } from 'react';
import axiosInstance from '@/axios/axiosInstance';
import { API_ENDPOINTS } from '@/api-endpoints';
import { initProductlane } from '@/lib/productlane';
import useAuthStore from '@/store/AuthStore';

/**
 * Loads the Productlane support widget for authenticated dashboard users,
 * identified via a backend-signed JWT (TELV1-483). The token is fetched
 * BEFORE the widget initializes because a `set({ userToken })` queued while
 * the widget script is still loading gets dropped — init-time tokens are the
 * only race-free path. No-op when VITE_PRODUCTLANE_WIDGET_KEY is unset; if
 * the token fetch fails, the widget starts anonymously — support keeps
 * working either way.
 */
export const useProductlaneWidget = () => {
  const { authToken } = useAuthStore();
  const started = useRef(false);

  const widgetKey = import.meta.env.VITE_PRODUCTLANE_WIDGET_KEY;

  useEffect(() => {
    if (!widgetKey || !authToken || started.current) return;
    started.current = true;

    (async () => {
      let userToken: string | undefined;
      try {
        const { data } = await axiosInstance.get<{ token: string | null }>(
          API_ENDPOINTS.PRODUCTLANE.WIDGET_TOKEN,
        );
        userToken = data?.token ?? undefined;
      } catch {
        // Anonymous fallback: identification failing must never break support.
      }
      initProductlane(widgetKey, userToken);
    })();
  }, [widgetKey, authToken]);
};
